import React, { useContext, useEffect, useState } from "react";

// Services
import AuthenticationService from "../../services/AuthenticationService";
import CountryContext from "../../CountryContext";
import { useIntl } from "react-intl";

// UI
import SearchSelect from "../../ui-components/SearchSelect";
import Placeholder from "../../ui-components/Placeholder";
import { PageHeading } from "../../ui-components/Container";
import RadioGroup from "../../ui-components/RadioGroup";
import DatePicker from "../../ui-components/DatePicker";
import Select from "../../ui-components/Select";
import Button from "../../ui-components/Button";
import Tabs from "../../ui-components/Tabs";

import {
  getSectionIcon,
  useInternalUsers,
  getVisibilityLevel,
  getVisibilityOptions,
  getAnalysisPeriod,
  getUsersOptions, getPeriodOptions,
} from "./Analytics.lib";

import {
  AnalysisPeriod,
  CustomPeriod,
  PeriodType,
  VisibilityLevel,
  VisibilityType,
} from "./Analytics.type";
import { AnalyticsWorksites } from "./Worksites";
import { AnalyticsUsers } from "./Users";
import { AnalyticsProducts } from "./Products";
import { AnalyticsGame } from "./Game";
import { AnalyticsConfigurations } from "./Configurations";
import { AnalyticsResales } from "./Resales";
import { WorksiteType } from "./Worksites/Worksites.type";
import useCountryAxiosInstance from "../../hooks/useCountryAxiosInstance";

const today = new Date();
const start = today.setMonth(today.getMonth() - 3);


export function Analytics() {

  const intl = useIntl();
  const {country} = useContext(CountryContext);
  const userData = AuthenticationService.getUserData();
  const countryApiRequest = useCountryAxiosInstance();

  const roleType = userData.role_type;
  const visibilityLevel: VisibilityLevel = getVisibilityLevel(roleType);
  const permission = visibilityLevel === 1 ? roleType : `user_${userData.id}`;
  const internalUsers = useInternalUsers(country.code, permission);
  const [worksiteTypes, setWorksiteTypes] = useState<
    WorksiteType[] | undefined
  >();

  const [previousWasResales, setPreviousWasResales] = useState<boolean>(false);
  const [tabIndex, setTabIndex] = useState<number>(0);
  const [period, setPeriod] = useState<PeriodType>(tabIndex === 4 ? "next_month": "last_month");
  const [analysisPeriod, setAnalysisPeriod] = useState<AnalysisPeriod>({
    start: "",
    end: "",
  });
  const [customPeriod, setCustomPeriod] = useState<CustomPeriod>({
    start: new Date(start),
    end: new Date(),
  });

  const allOption = { label: intl.formatMessage({ id: "all" }), value: 0 };
  const [visibility, setVisibility] =
    useState<VisibilityType>("visibility_full");
  const [visibilityUser, setVisibilityUser] = useState<{
    label: string;
    value: number;
  }>(allOption);

  let tabNames = [
    "worksites",
    "configurations",
    "users",
    "products",
    "sales",
  ];
  if (visibilityLevel <= 3) {
    tabNames.push("game");
  }

  useEffect(() => {
    setVisibilityUser(allOption);
  }, [visibility]);

  useEffect(() => {
    setAnalysisPeriod(getAnalysisPeriod(period, customPeriod));
  }, [period, customPeriod]);

  useEffect(() => {
    if (tabIndex === 4) {
      setPeriod("next_month");
      const _today = new Date();
      setCustomPeriod({
        start: new Date(today.setMonth(_today.getMonth())),
        end: new Date(today.setMonth(_today.getMonth() + 3)),
      })
      setPreviousWasResales(true);
    } else {
      if (previousWasResales) {
        setPeriod("last_month");
        setCustomPeriod({
          start: new Date(start),
          end: new Date(),
        });
      }
      setPreviousWasResales(false);
    }
  }, [tabIndex]);

  useEffect(() => {
    if (!worksiteTypes) {
      // @ts-ignore
      countryApiRequest
        .get(`/worksite_types/all`)
        .then((res) => {
          // @ts-ignore
          setWorksiteTypes(res.sort((a, b) => (a.order > b.order ? 1 : -1)));
        })
        // @ts-ignore
        .catch((err) => console.log(err));
    }
  }, []);

  const showCurrentTab = (tabIndex: number) => {
    switch (tabIndex) {
      case 0:
        return (
          <AnalyticsWorksites
            visibilityUserId={visibilityUser.value}
            analysisPeriod={analysisPeriod}
            internalUsers={internalUsers ?? []}
            worksiteTypes={worksiteTypes}
          />
        );
      case 1:
        return <AnalyticsConfigurations
          visibilityUserId={visibilityUser.value}
          analysisPeriod={analysisPeriod}
        />
      case 2:
        return (
          <AnalyticsUsers
            visibilityLevel={visibilityLevel}
            visibilityUserId={visibilityUser.value}
            analysisPeriod={analysisPeriod}
          />
        );
      case 3:
        return (
          <AnalyticsProducts
            visibilityUserId={visibilityUser.value}
            analysisPeriod={analysisPeriod}
            worksiteTypes={worksiteTypes}
          />
        );
      case 4:
        return (
          <AnalyticsResales
            visibilityLevel={visibilityLevel}
            visibilityUserId={visibilityUser.value}
            analysisPeriod={analysisPeriod}
          />
        );
      case 5:
        return (
          <AnalyticsGame
            visibilityLevel={visibilityLevel}
            visibilityUserId={visibilityUser.value}
            analysisPeriod={analysisPeriod}
            worksiteTypes={worksiteTypes}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-full pb-12 flex flex-col bg-white">
      <main className="flex-grow flex flex-col justify-center w-full mx-auto">
        <PageHeading
          title={intl.formatMessage({ id: "analytics" })}
          contentActions={
            <div>
              <div className="sm:hidden">
                <Tabs
                  setActiveTab={setTabIndex}
                  active={tabIndex}
                  tabs={tabNames.map((i) => ({
                    name: intl.formatMessage({ id: i }),
                  }))}
                />
              </div>
              <div className="hidden sm:flex">
                {tabNames.map((name, index) => {
                  const Icon = getSectionIcon(name);
                  return (
                    <div key={name} className="mr-4">
                      <Button
                        className="whitespace-nowrap"
                        styleType={tabIndex === index ? "default" : "empty"}
                        onClick={() => {
                          setTabIndex(index);
                        }}
                        full={undefined}
                      >
                        <div className="flex flex-row gap-2 items-center">
                          <div>
                            <Icon className="h-4 w-4" />
                          </div>
                          <span>{intl.formatMessage({ id: name })}</span>
                        </div>
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          }
        />
        {internalUsers && worksiteTypes ? (
          <div className="flex flex-col gap-1">
            <h1 className="text-gray-600 mt-2">
              {intl.formatMessage({
                id: "target_period",
                defaultMessage: "Periodo di riferimento",
              })}
            </h1>
            <div className="flex flex-col xl:flex-row w-full">
              <div>
                <RadioGroup
                  id="periodSelection"
                  title={undefined}
                  description={undefined}
                  horizontal
                  options={getPeriodOptions(tabIndex).map((i) => ({
                    value: i,
                    name: intl.formatMessage({ id: i }),
                  }))}
                  currentValue={period}
                  onChange={(value: PeriodType) => {
                    setPeriod(value);
                  }}
                />
              </div>
              {period === "custom" ? (
                <div className="ml-12 flex flex-row gap-2 sm:gap-6 justify-center">
                  <div className="w-36">
                    <DatePicker
                      label="Inizio"
                      value={customPeriod.start}
                      onChange={(i: Date) =>
                        setCustomPeriod({ ...customPeriod, start: i })
                      }
                      minDate={
                        tabIndex === 4
                          ? null
                          : new Date((new Date().getFullYear() - 3).toString())
                      }
                      maxDate={tabIndex === 4 ? null : new Date()}
                    />
                  </div>
                  <div className="w-36">
                    <DatePicker
                      label="Fine"
                      value={customPeriod.end}
                      onChange={(i: Date) =>
                        setCustomPeriod({ ...customPeriod, end: i })
                      }
                      minDate={
                        tabIndex === 4
                          ? null
                          : new Date((new Date().getFullYear() - 3).toString())
                      }
                      maxDate={tabIndex === 4 ? null : new Date()}
                    />
                  </div>
                </div>
              ) : null}
            </div>
            <h1
              className={`text-gray-600 mt-2 flex ${
                visibilityLevel === 4 || (tabIndex === 5 && visibilityLevel === 3) ? "hidden" : ""
              }`}
            >
              Visibilità
            </h1>
            <div
              className={`flex flex-col md:flex-row gap-3 w-full ${
                visibilityLevel === 4 ? "hidden" : ""
              }`}
            >
              {visibilityLevel !== 4 && (
                <div>
                  <Select
                    label={undefined}
                    className={undefined}
                    value={visibility}
                    options={getVisibilityOptions(visibilityLevel, intl)}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setVisibility(e.target.value as VisibilityType)
                    }
                  />
                </div>
              )}
              {visibility !== "visibility_full" ? (
                <div>
                  {/*@ts-ignore*/}
                  <SearchSelect
                    value={visibilityUser}
                    options={getUsersOptions(visibility, internalUsers, intl)}
                    onChange={(e: any) => setVisibilityUser(e)}
                  />
                </div>
              ) : null}
            </div>
            {showCurrentTab(tabIndex)}
          </div>
        ) : (
          <div className="flex w-full justify-center mt-2">
            <div className="w-full flex flex-col gap-3">
              <Placeholder height="h-10" />
              <Placeholder height="h-10" />
              <div className="flex flex-col w-full mb-4 gap-y-5">
                <div className="flex flex-row gap-x-5">
                  <Placeholder width="w-1/2" height="h-60" />
                  <Placeholder width="w-1/2" height="h-60" />
                </div>
                <div className="flex flex-row gap-x-5">
                  <Placeholder width="w-1/2" height="h-60" />
                  <Placeholder width="w-1/2" height="h-60" />
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
